import { LocationProps, ResponseProps } from 'types/CommonTypes'
import { VehicleProps } from 'types/CommunityTypes'
import {
  DeleteStudentOutputProps,
  UpsertStudentInputProps,
  UpsertStudentOutputProps,
  UpsertStudentProps,
} from 'types/StudentTypes'
import { UserSmsProps } from 'types/UserTypes'
import { httpBearerAuth, httpCodeAuth, processError } from 'utils/requests'

export const verifyStudent = async (schoolName: string, code: string): Promise<ResponseProps> => {
  try {
    const { data } = await httpBearerAuth('post', `/api/verifyStudent`, schoolName, { code })
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

export const registerStudent = async (
  schoolName: string,
  code: string,
  userRelationship: string,
  scheduleGroupName: string,
  pickupLocation: LocationProps,
  dropoffLocation: LocationProps,
): Promise<ResponseProps> => {
  try {
    const { data } = await httpBearerAuth('post', `/api/registerStudent`, schoolName, {
      code,
      userRelationship,
      scheduleGroupName,
      pickupLocation,
      dropoffLocation,
    })
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

export const upsertStudents = async (
  students: UpsertStudentProps[],
  sms: UserSmsProps = 'SUPPRESS',
  registrationStatus?: 'REGISTERED' | 'UNVERIFIED',
): Promise<ResponseProps<UpsertStudentOutputProps>> => {
  try {
    const body: UpsertStudentInputProps = { students, sms, registrationStatus }
    const { data } = await httpCodeAuth('put', `/api/students`, body as unknown as Record<string, unknown>, 30000)
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

export const updateStudentSettings = async (
  schoolName: string,
  studentId: string,
  userName?: string,
  userRelationship?: string,
  studentName?: string,
  scheduleGroupName?: string,
): Promise<ResponseProps> => {
  try {
    const body = {
      ...(userName ? { userName } : {}),
      ...(userRelationship ? { userRelationship } : {}),
      ...(scheduleGroupName ? { scheduleGroupName } : {}),
      // check for non-null since falsy values are allowed
      ...(studentName != null ? { studentName } : {}),
    }
    const { data } = await httpBearerAuth('patch', `/api/students/${studentId}/settings`, schoolName, body)
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

export const updateStudentMorningSettings = async (
  schoolName: string,
  studentId: string,
  pickupLocation: LocationProps,
  pickupVehicle?: VehicleProps,
  pickupSeatsFilled?: number | null,
  pickupSeatsAvailable?: number | null,
  pickupComments?: string,
): Promise<ResponseProps> => {
  try {
    await httpBearerAuth('patch', `/api/students/${studentId}/settings`, schoolName, {
      pickupLocation,
      pickupVehicle,
      ...(pickupSeatsFilled != null ? { pickupSeatsFilled } : {}),
      ...(pickupSeatsAvailable != null ? { pickupSeatsAvailable } : {}),
      pickupComments,
    })
    return {}
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

export const updateStudentAfternoonSettings = async (
  schoolName: string,
  studentId: string,
  dropoffLocation: LocationProps,
  dropoffVehicle?: VehicleProps,
  dropoffSeatsFilled?: number | null,
  dropoffSeatsAvailable?: number | null,
  dropoffComments?: string,
  studentActivities?: string[],
): Promise<ResponseProps> => {
  try {
    const isEnabled = dropoffLocation && dropoffLocation?.lat && dropoffLocation?.lng ? true : false
    await httpBearerAuth('patch', `/api/students/${studentId}/settings`, schoolName, {
      dropoffLocation,
      dropoffVehicle,
      ...(dropoffSeatsFilled != null ? { dropoffSeatsFilled } : {}),
      ...(dropoffSeatsAvailable != null ? { dropoffSeatsAvailable } : {}),
      dropoffComments: isEnabled ? dropoffComments : '',
      studentActivities: isEnabled ? studentActivities : [],
    })
    return {}
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

export const deleteStudents = async (students: readonly string[]): Promise<ResponseProps<DeleteStudentOutputProps>> => {
  try {
    const body = { students }
    const { data } = await httpCodeAuth('delete', `/api/students`, body, 30000)
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

export const deleteStudent = async (schoolName: string, studentId: string): Promise<ResponseProps> => {
  try {
    const { data } = await httpBearerAuth('delete', `/api/students/${studentId}`, schoolName)
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

export const getStudents = async (): Promise<ResponseProps> => {
  try {
    const { data } = await httpBearerAuth('get', `/api/students`)
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}
